import {
  SYSTEM_COLUMN_DISPLAY_NAME,
  SYSTEM_GENERATED_COLUMN_TYPES,
  TableColumnForTableExport,
  getDataTypeForSystemGeneratedColumn,
  getSystemGeneratedColumnTypesFromColumns,
} from '@doss/common-all'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControlLabel,
  Stack,
  Theme,
  Typography,
  accordionSummaryClasses,
  useTheme,
} from '@mui/material'
import { last, some, sortBy } from 'lodash'
import { useMemo, useState } from 'react'
import { RxChevronRight } from 'react-icons/rx'
import { TbCsv } from 'react-icons/tb'
import { Toggle } from '~/@/components/inputs/standalone/toggle'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { DetailedTableColumn, DetailedTableTab } from '~/components/tables/types'
import { Text } from '~/components/ui/atoms/text/Text'

function getStyles(theme: Theme) {
  return {
    accordion: {
      backgroundColor: 'transparent',
      width: '100%',
    },
    accordionSummary: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-start',
      padding: 0,
      marginInlineStart: '0',
      marginTop: '0',
      marginInlineEnd: '0',
      marginBottom: '16px',
      '&:hover': {
        borderRadius: '2px',
        backgroundColor: theme.palette.common.doss.gray[1],
      },
      '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
        transform: 'rotate(90deg)',
      },
      [`& .${accordionSummaryClasses.expandIconWrapper}`]: {
        marginInlineEnd: '10px',
      },
      '& .MuiFormControlLabel-root': {
        gap: theme.spacing(1),
        margin: theme.spacing(0),
        width: '100%',
      },
    },
    accordionDetails: {
      display: 'flex',
      flexDirection: 'column',
      padding: 0,
      '& .MuiFormControlLabel-root': {
        gap: theme.spacing(1),
        margin: theme.spacing(0),
        width: '100%',
      },
    },
  }
}

interface ExportCSVAccordionProps {
  name: string
  columnHeaders: DetailedTableColumn[]
  selectedTabForExport: DetailedTableTab | null
  handleColumnChange: (column: TableColumnForTableExport, isChecked: boolean) => void
  setIncludeRecordId: (includeRecordId: boolean) => void
  selectedColumns: TableColumnForTableExport[]
  setSelectedColumns: (columns: TableColumnForTableExport[]) => void
}

/**
 * Accordion for configuraton of CSV export, nested within the Export Table Dialog because we need
 * to access the context of the table menu without unnecessary prop drilling.
 */
export function ExportCSVAccordion(props: ExportCSVAccordionProps) {
  const { t } = useTypedTranslation()
  const styles = getStyles(useTheme())
  const { isAdmin, isInternal } = useGlobalContext()
  const {
    columnHeaders,
    selectedTabForExport,
    handleColumnChange,
    setIncludeRecordId,
    selectedColumns,
    setSelectedColumns,
  } = props

  const [expanded, setExpanded] = useState(true)

  const sortedColumnHeaders = useMemo(
    () => sortBy(columnHeaders, (column) => column.order),
    [columnHeaders],
  )

  // If user is an admin or internal user, show all system generated columns.
  // Otherwise, show only the system generated columns that are visible on the table tab.
  const systemGeneratedColumns: TableColumnForTableExport[] = useMemo(() => {
    const selectedTabColumns = selectedTabForExport?.columns ?? []
    const systemGeneratedColumnTypes =
      isAdmin || isInternal
        ? SYSTEM_GENERATED_COLUMN_TYPES
        : getSystemGeneratedColumnTypesFromColumns(selectedTabColumns)

    return systemGeneratedColumnTypes.map((systemGeneratedColumnType, index) => {
      const { dataType, indexType } = getDataTypeForSystemGeneratedColumn(systemGeneratedColumnType)
      const lastColumnOrder = last(sortedColumnHeaders)?.order ?? 0
      return {
        id: systemGeneratedColumnType,
        name: SYSTEM_COLUMN_DISPLAY_NAME[systemGeneratedColumnType],
        order: lastColumnOrder + index + 1,
        type: dataType,
        innerType: null,
        indexType,
        resolvedSourceType: dataType,
        indexPrefix: '',
        indexSuffix: '',
        lookupColumn: null,
      }
    })
  }, [isAdmin, isInternal, selectedTabForExport?.columns, sortedColumnHeaders])

  const allColumns = useMemo(() => {
    return [...sortedColumnHeaders, ...systemGeneratedColumns]
  }, [sortedColumnHeaders, systemGeneratedColumns])

  const columnIsSelected = (column: TableColumnForTableExport): boolean => {
    return some(selectedColumns, (selectedColumn) => {
      return selectedColumn.id === column.id
    })
  }

  return (
    <Accordion disableGutters elevation={0} expanded={expanded} sx={styles.accordion}>
      <AccordionSummary
        expandIcon={<RxChevronRight />}
        onClick={() => setExpanded(!expanded)}
        sx={styles.accordionSummary}
      >
        <Stack alignItems="center" direction="row" justifyContent="flex-start" spacing={1}>
          <TbCsv size="16px" />
          <Text typography="body2">{t('table.export.export_as_csv')}</Text>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={styles.accordionDetails}>
        <Stack direction="column" gap={2}>
          <FormControlLabel
            control={
              <Toggle
                defaultChecked={false}
                onCheckedChange={(checked) => setIncludeRecordId(checked)}
              />
            }
            label={t('table.export.include_record_id')}
          />

          <Box>
            <Stack direction="column" gap={0.5}>
              <FormControlLabel
                control={
                  <Toggle
                    checked={selectedColumns.length === allColumns.length}
                    onCheckedChange={(checked) =>
                      checked ? setSelectedColumns(allColumns) : setSelectedColumns([])
                    }
                  />
                }
                label={t('table.export.select_all_columns')}
              />
              <Typography variant="body1">{t('table.export.select_columns_to_include')}</Typography>
              {sortedColumnHeaders.map((column) => (
                <FormControlLabel
                  key={column.id}
                  control={
                    <Toggle
                      checked={columnIsSelected(column)}
                      onCheckedChange={(checked) => handleColumnChange(column, checked)}
                    />
                  }
                  label={column.name}
                />
              ))}
              {systemGeneratedColumns.map((column) => (
                <FormControlLabel
                  key={column.id}
                  control={
                    <Toggle
                      checked={columnIsSelected(column)}
                      onCheckedChange={(checked) => handleColumnChange(column, checked)}
                    />
                  }
                  label={column.name}
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}
